import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { Settings } from '../../../../core';
import { Category, CategoryNaturalKey, Report } from '../../../../documents';
import { Project, Protocol } from '../../../shared';
import { ELearningUploadStorage } from '../../storages/e-learning-upload-storage';
import { UploadDialogData, UPLOAD_DLG_DATA_TOKEN } from '../base/upload-dialog-data';
import { SitesData } from '../form-fields/sites-select.component';
import { SiteSpecificUploadDialogBase } from './site-specific-upload-dialog-base';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { UploadedDocumentsStorage } from '../../storages/uploaded-documents-storage';
import { isEmpty } from 'lodash';

@Component({
    selector: 'app-study-training-materials-upload',
    template: require('./training-materials-upload.component.html'),
    styles: [require('./training-materials-upload.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingMaterialsUploadComponent extends SiteSpecificUploadDialogBase {
    public readonly subcategoryControl: FormControl;
    public readonly protocolControl: FormControl;
    public readonly projectsControl: FormControl;
    public readonly versionNumberControl: FormControl;
    public readonly titleControl: FormControl;
    public sitesFormGroup: FormGroup;
    public readonly subcategories$: Observable<Category[]>;

    public allowedFileExtensions: string[];
    public sitesData: SitesData;
    public selectedProtocols$: Observable<Protocol[]>;

    public get showSitesSelect() {
        return this.editorForm.get('sites') != null;
    }

    public get categoryChangeConfirmationText(): string {
        const controlsWithValue = [];

        if (this.selectedFiles && this.selectedFiles.size > 0) {
            controlsWithValue.push('Files');
        }
        if (this.sitesData && (this.sitesData.allSites || !isEmpty(this.sitesData.selectedSites))) {
            controlsWithValue.push('Sites');
        }

        return controlsWithValue.length > 0 ? `${controlsWithValue.join(' and ')} will be cleared.` : null;
    }

    private readonly trainingMaterialsOthersId: CategoryNaturalKey = 'training-materials-others';
    private readonly trainingMaterialsELearningId: CategoryNaturalKey = 'training-materials-e-learning';

    constructor(
        protected readonly dialog: MdlDialogReference,
        protected readonly dialogService: MdlDialogService,
        @Inject(UPLOAD_DLG_DATA_TOKEN) protected readonly dialogData: UploadDialogData<Report>,
        uploadedDocumentsStorage: UploadedDocumentsStorage,
        eLearningUploadStorage: ELearningUploadStorage,
        protected readonly settings: Settings
    ) {
        super(dialog, dialogService, uploadedDocumentsStorage, eLearningUploadStorage);

        this.subcategoryControl = new FormControl();
        this.protocolControl = new FormControl();
        this.projectsControl = new FormControl();
        this.sitesFormGroup = new FormGroup({});
        this.titleControl = new FormControl();
        this.versionNumberControl = new FormControl();
        this.subcategories$ = dialogData.subCategories$;

        this.subcategoryControl.valueChanges
            .pipe(takeUntil(this.onDialogDestroying))
            .subscribe((subcategoryId: CategoryNaturalKey) => {
                this.sitesFormGroup = new FormGroup({});
                this.sitesData = null;
                super.resetFilesControl();

                switch (subcategoryId) {
                    case this.trainingMaterialsOthersId:
                        this.allowedFileExtensions = settings.uploadedFileExtensionsByCategoryId(this.trainingMaterialsOthersId);
                        this.editorForm.addControl('subcategory', this.subcategoryControl);
                        this.editorForm.addControl('protocols', this.protocolControl);
                        this.editorForm.addControl('projects', this.projectsControl);
                        this.editorForm.addControl('sites', this.sitesFormGroup);
                        this.editorForm.addControl('title', this.titleControl);
                        this.editorForm.addControl('versionNumber', this.versionNumberControl);
                        break;

                    case this.trainingMaterialsELearningId:
                        this.allowedFileExtensions = settings.uploadedFileExtensionsByCategoryId(this.trainingMaterialsELearningId);
                        this.editorForm.addControl('subcategory', this.subcategoryControl);
                        this.editorForm.addControl('protocols', this.protocolControl);
                        this.editorForm.addControl('projects', this.projectsControl);
                        this.editorForm.removeControl('sites');
                        this.editorForm.addControl('title', this.titleControl);
                        this.editorForm.addControl('versionNumber', this.versionNumberControl);
                        break;
                }
            });
        this.selectedProtocols$ = this.protocolControl.valueChanges.pipe(distinctUntilChanged());
    }

    public onSitesChange(sitesData: SitesData) {
        this.sitesData = sitesData;
    }

    public async addFiles() {
        const projects = this.projectsControl.value as Project[];
        const projectNumberStr = this.getProjectNumbers(projects);

        const message = `The document will be posted to ${projects[0].sponsor} - ${projects[0].protocolNumber} - ${projectNumberStr}.Proceed?`;

        let documentMetadata: { [key: string]: any };
        if (this.subcategoryControl.value === this.trainingMaterialsOthersId) {
            documentMetadata = {
                projects: projects,
                allSites: this.sitesData.allSites,
                selectedSites: this.sitesData.selectedSites,
                title: this.titleControl.value,
                versionNumber: this.versionNumberControl.value
            };
        } else if (this.subcategoryControl.value === this.trainingMaterialsELearningId) {
            documentMetadata = {
                projects: projects,
                allSites: true,
                title: this.titleControl.value,
                versionNumber: this.versionNumberControl.value
            };
        }

        super.addFiles(message, this.subcategoryControl.value, documentMetadata);
    }

    private getProjectNumbers(projects: Project[]): string {
        let projectStr: string[] = [];

        projects.forEach(project => {
            projectStr.push(project.zcProjectNumber);
        });

        return projectStr.join(',');
    }
}
